import React, { useCallback, useEffect, useState } from "react";
import { api, API, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Paperclip, CaretDown, CaretRight, FilePdf, EnvelopeSimple, ArrowsClockwise, FileArrowDown, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Attachments } from "@/components/Attachments";
import { CsvExport } from "@/components/CsvExport";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";
import { VendorPoUploadSheet, MaterialPicker, PriceFlag, VendorPicker } from "@/pages/vendor-pos/VendorPoUploadSheet";

const GST_RATES = [0, 5, 12, 18, 28];
const VPO_STATUSES = ["draft", "sent", "partially_received", "received", "closed", "cancelled"];
const emptyItem = { description: "", hsn: "", qty: 1, unit: "Nos", unit_price: 0, gst_rate: 18 };

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function VendorPOs({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const emptyForm = {
    project_id: projectId || "", vendor: "", vendor_email: "", vendor_gstin: "", vendor_address: "",
    ship_to: "", ship_to_address: "", payment_terms: "", delivery_terms: "",
    issue_date: new Date().toISOString().slice(0, 10), expected_date: "",
    items: [{ ...emptyItem }], status: "draft", terms: "", notes: "",
    auto_send_email: true,
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => Promise.all([
    api.get("/vendor-pos", { params: projectId ? { project_id: projectId } : {} }),
    api.get("/projects"),
  ]).then(([a, b]) => { setRows(a.data); setProjects(b.data); }), [projectId]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const preview = form.items.reduce((acc, i) => {
    const line = Number(i.qty || 0) * Number(i.unit_price || 0);
    const rate = Number(i.gst_rate || 0);
    return { subtotal: acc.subtotal + line, tax: acc.tax + line * rate / 100 };
  }, { subtotal: 0, tax: 0 });

  const submit = async () => {
    try {
      const res = await api.post("/vendor-pos", {
        ...form,
        project_id: projectId || form.project_id || null,
        expected_date: form.expected_date || null,
        items: form.items.map((i) => ({
          description: i.description, hsn: i.hsn || "", unit: i.unit || "Nos",
          qty: Number(i.qty), unit_price: Number(i.unit_price), gst_rate: Number(i.gst_rate || 0),
        })),
      });
      toast.success("Vendor PO created");
      const emailStatus = res.data?.auto_email_status;
      if (form.auto_send_email && form.vendor_email) {
        if (emailStatus === "sent") toast.success(`Emailed to ${form.vendor_email}`);
        else if (emailStatus === "not_configured") toast.warning("Email not sent — SendGrid not configured");
        else if (emailStatus === "failed") toast.error("PO saved but email delivery failed. Retry from the row action.");
      }
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const resend = async (v) => {
    const to = window.prompt(`Send PO ${v.po_number} to which email?`, v.vendor_email || "");
    if (!to) return;
    try {
      await api.post(`/vendor-pos/${v.id}/send-email`, { recipient: to });
      toast.success(`Emailed to ${to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadPdf = async (v) => {
    const res = await fetch(`${API}/vendor-pos/${v.id}/pdf`, {
      credentials: "include",
    });
    if (!res.ok) { toast.error("PDF download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${v.po_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vendor PO?")) return;
    try { await api.delete(`/vendor-pos/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  const updateStatus = async (v, newStatus) => {
    try {
      await api.put(`/vendor-pos/${v.id}`, {
        project_id: v.project_id, vendor: v.vendor, vendor_gstin: v.vendor_gstin,
        vendor_address: v.vendor_address, issue_date: v.issue_date, expected_date: v.expected_date,
        items: v.items, status: newStatus, terms: v.terms, notes: v.notes,
      });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="vendor-pos-page">
      {!embedded && (
      <PageHeader
        title="Vendor Purchase Orders"
        subtitle="POs issued to vendors — track delivery, GST and receipts."
        module="Procurement · MM"
        ribbon={
          <>
            <BcRibbonGroup label="New">
              <BcRibbonBtn icon={UploadSimple} label="Upload Proforma" onClick={() => setUploadOpen(true)} testid="ribbon-upload-vpo" />
              <BcRibbonBtn icon={Plus} label="New Vendor PO" variant="primary" onClick={() => setOpen(true)} testid="ribbon-new-vpo" />
            </BcRibbonGroup>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
            </BcRibbonGroup>
            <BcRibbonGroup label="Report">
              <CsvExport resource="vendor-pos" filename="vendor-pos.csv" render={({ onClick }) => (
                <BcRibbonBtn icon={FileArrowDown} label="Export CSV" onClick={onClick} testid="ribbon-export-csv" />
              )} />
            </BcRibbonGroup>
          </>
        }
      />
      )}
      <VendorPoUploadSheet
        projects={projects}
        onUploaded={load}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        hideTrigger
      />
      <Sheet open={open} onOpenChange={setOpen}>
        <VpoFormSheet form={form} setForm={setForm} projects={projects} preview={preview} setItem={setItem} submit={submit} projectId={projectId} />
      </Sheet>
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">Vendor POs ({rows.length})</div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button size="sm" data-testid="create-vpo-button" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-8">
                <Plus size={14} className="mr-1" /> New Vendor PO
              </Button>
            </SheetTrigger>
            <VpoFormSheet form={form} setForm={setForm} projects={projects} preview={preview} setItem={setItem} submit={submit} projectId={projectId} />
          </Sheet>
        </div>
      )}

      <div className={embedded ? "" : "p-6"}>
        {rows.length === 0 ? (
          <EmptyState title="No vendor POs yet" description="Issue POs to vendors, track expected delivery, and attach signed copies." />
        ) : (
          <div className="bc-card overflow-hidden" data-testid="vendor-pos-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>PO #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((v) => (
                  <React.Fragment key={v.id}>
                    <TableRow data-testid={`vpo-row-${v.id}`}>
                      <TableCell>
                        <button onClick={() => setExpanded(expanded === v.id ? null : v.id)} data-testid={`expand-vpo-${v.id}`} className="text-slate-400 hover:text-[#EA580C] p-0.5">
                          {expanded === v.id ? <CaretDown size={14} /> : <CaretRight size={14} />}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono-num font-medium">{v.po_number}</TableCell>
                      <TableCell>{v.vendor}</TableCell>
                      <TableCell className="text-slate-600">{projMap[v.project_id] || "—"}</TableCell>
                      <TableCell className="text-slate-600">{fmtDate(v.issue_date)}</TableCell>
                      <TableCell className="text-slate-600">{v.expected_date ? fmtDate(v.expected_date) : "—"}</TableCell>
                      <TableCell>
                        <Select value={v.status} onValueChange={(newStatus) => updateStatus(v, newStatus)}>
                          <SelectTrigger className="h-7 px-2 text-xs" data-testid={`vpo-status-${v.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{VPO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(v.total)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button onClick={() => resend(v)} data-testid={`resend-vpo-${v.id}`} title="Email vendor" className="text-slate-500 hover:text-[#EA580C] p-1"><EnvelopeSimple size={14} /></button>
                          <button onClick={() => downloadPdf(v)} data-testid={`pdf-vpo-${v.id}`} title="Download PDF" className="text-slate-500 hover:text-[#EA580C] p-1"><FilePdf size={14} /></button>
                          <button onClick={() => del(v.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded === v.id && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={9} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <div className="label-caps mb-2">Line items</div>
                              <table className="w-full text-sm font-mono-num">
                                <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500">
                                  <th className="text-left p-1">Item</th><th className="text-left p-1">HSN</th>
                                  <th className="text-right p-1">Qty</th><th className="text-right p-1">Rate</th>
                                  <th className="text-right p-1">GST%</th><th className="text-right p-1">Amount</th>
                                </tr></thead>
                                <tbody>{v.items.map((i, idx) => (
                                  <tr key={`${v.id}-item-${idx}-${i.description || ""}`} className="border-t border-slate-200">
                                    <td className="p-1">{i.description}</td>
                                    <td className="p-1 text-slate-500">{i.hsn || "—"}</td>
                                    <td className="p-1 text-right">{i.qty}</td>
                                    <td className="p-1 text-right">{fmtMoney(i.unit_price)}</td>
                                    <td className="p-1 text-right">{i.gst_rate}%</td>
                                    <td className="p-1 text-right">{fmtMoney(Number(i.qty) * Number(i.unit_price))}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                              <div className="mt-3 text-sm space-y-1 font-mono-num">
                                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtMoney(v.subtotal)}</span></div>
                                <div className="flex justify-between text-slate-600"><span>Tax</span><span>{fmtMoney(v.tax_amount)}</span></div>
                                <div className="flex justify-between font-bold text-[#EA580C] border-t border-slate-200 pt-1"><span>Total</span><span>{fmtMoney(v.total)}</span></div>
                              </div>
                              {v.terms && <div className="mt-3"><div className="label-caps">Terms</div><div className="text-xs text-slate-600 mt-1">{v.terms}</div></div>}
                            </div>
                            <div>
                              <div className="label-caps mb-2 flex items-center gap-1"><Paperclip size={12} /> Attachments</div>
                              <Attachments parentType="vendor_po" parentId={v.id} />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function VpoFormSheet({ form, setForm, projects, preview, setItem, submit, projectId }) {
  const [bench, setBench] = useState({});
  const [benchLoading, setBenchLoading] = useState(false);
  const [vendorGst, setVendorGst] = useState(18);  // vendor's typical GST% for new lines
  const checkPrices = useCallback(async (items, vendorName) => {
    const list = (items || []).filter((i) => (i.description || "").trim());
    if (!list.length) { setBench({}); return; }
    setBenchLoading(true);
    try {
      const { data } = await api.post("/purchase-analytics/price-benchmark", {
        exclude_vendor: vendorName || "",
        items: list.map((i) => ({ description: i.description, hsn: i.hsn || "", unit_price: Number(i.unit_price) || 0 })),
      });
      const map = {};
      (data.rows || []).forEach((r) => { map[(r.description || "").trim().toLowerCase()] = r; });
      setBench(map);
    } catch { /* benchmark is advisory */ } finally { setBenchLoading(false); }
  }, []);
  const descKey = (form.items || []).map((i) => (i.description || "").trim().toLowerCase()).join("|");
  useEffect(() => {
    const t = setTimeout(() => checkPrices(form.items, form.vendor), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descKey, form.vendor]);
  return (
    <SheetContent className="w-[760px] sm:max-w-2xl overflow-y-auto">
      <SheetHeader><SheetTitle className="font-display">Create Vendor PO</SheetTitle></SheetHeader>
      <div className="space-y-4 mt-6">
        <Field label="Vendor">
          <VendorPicker
            value={form.vendor}
            onChange={(name) => setForm({ ...form, vendor: name })}
            onSelectVendor={(v) => {
              const g = Number(v.gst_rate) || 18;
              setVendorGst(g);
              setForm({
                ...form,
                vendor: v.name,
                vendor_gstin: v.gstin || form.vendor_gstin,
                vendor_email: v.email || form.vendor_email,
                vendor_address: v.address || form.vendor_address,
                payment_terms: v.payment_terms || form.payment_terms,
                delivery_terms: v.delivery_terms || form.delivery_terms,
                terms: v.terms || form.terms,
                items: (form.items || []).map((it) => (!(it.description || "").trim() ? { ...it, gst_rate: g } : it)),
              });
            }}
            testid="vpo-vendor-input"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor email">
            <Input type="email" data-testid="vpo-vendor-email-input" placeholder="vendor@example.com"
              value={form.vendor_email} onChange={(e) => setForm({ ...form, vendor_email: e.target.value })} />
          </Field>
          <Field label="Vendor GSTIN"><Input value={form.vendor_gstin} onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value.toUpperCase() })} /></Field>
        </div>
        {!projectId && (
          <Field label="Project (site)">
            <Select value={form.project_id || "__none__"} onValueChange={(v) => {
              const pid = v === "__none__" ? "" : v;
              const proj = projects.find((p) => p.id === pid);
              const siteAddr = proj ? ([proj.site, proj.location].filter(Boolean).join(", ") || proj.client_address || "") : "";
              setForm({
                ...form,
                project_id: pid,
                ...(siteAddr ? { ship_to_address: siteAddr, ship_to: form.ship_to || proj.name || "Site" } : {}),
              });
            }}>
              <SelectTrigger data-testid="vpo-manual-project-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Vendor address"><Textarea rows={2} value={form.vendor_address} onChange={(e) => setForm({ ...form, vendor_address: e.target.value })} /></Field>
        <Field label="Delivery address (Ship to)">
          <Textarea
            rows={2}
            data-testid="vpo-manual-shipto-input"
            placeholder="Delivery site / project address — shown as Consignee (Ship to) on the PO"
            value={form.ship_to_address}
            onChange={(e) => setForm({ ...form, ship_to_address: e.target.value, ship_to: e.target.value ? (form.ship_to || "Site") : "" })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Issue date"><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></Field>
          <Field label="Expected delivery"><Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></Field>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="label-caps block">Line items</Label>
            <MaterialPicker onPick={(m) => setForm({ ...form, items: [...form.items, {
              description: m.description || "", hsn: m.hsn || "", qty: 1, unit: m.unit || "Nos",
              unit_price: Number(m.last_unit_price) || 0, gst_rate: Number(m.gst_rate) || vendorGst,
            }] })} />
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_52px_48px_42px_60px_60px_104px_22px] gap-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
              <span>Description</span><span>HSN</span><span>Qty</span><span>Unit</span><span>Rate</span><span>GST%</span><span className="text-right">vs History</span><span></span>
            </div>
            {form.items.map((it, idx) => (
              <div key={`vp-${idx}`} className="grid grid-cols-[1fr_52px_48px_42px_60px_60px_104px_22px] gap-2 items-center">
                <MaterialAutocomplete
                  value={it.description}
                  onChange={(v) => setItem(idx, { description: v })}
                  onPick={(m) => setItem(idx, {
                    description: m.description || "",
                    hsn: m.hsn || it.hsn || "",
                    unit: m.unit || it.unit || "Nos",
                    unit_price: Number(m.last_unit_price) || Number(it.unit_price) || 0,
                    gst_rate: Number(m.gst_rate) || Number(it.gst_rate) || vendorGst,
                  })}
                  placeholder="Search material…"
                  testid={`vpo-manual-desc-${idx}`}
                />
                <Input placeholder="HSN" value={it.hsn} onChange={(e) => setItem(idx, { hsn: e.target.value })} data-testid={`vpo-manual-hsn-${idx}`} />
                <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} />
                <Input placeholder="Unit" value={it.unit || "Nos"} onChange={(e) => setItem(idx, { unit: e.target.value })} data-testid={`vpo-manual-unit-${idx}`} />
                <Input type="number" placeholder="Price" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })} />
                <Select value={String(it.gst_rate)} onValueChange={(v) => setItem(idx, { gst_rate: Number(v) })}>
                  <SelectTrigger className="h-9 px-2"><SelectValue /></SelectTrigger>
                  <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex justify-end min-w-0 overflow-hidden" data-testid={`vpo-manual-history-${idx}`}>
                  <PriceFlag r={bench[(it.description || "").trim().toLowerCase()]} loading={benchLoading} rate={Number(it.unit_price) || 0} onMatch={(val) => setItem(idx, { unit_price: val })} />
                </div>
                <button onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
              </div>
            ))}
            <button onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem, gst_rate: vendorGst }] })} className="text-xs text-[#EA580C] font-semibold">+ ADD LINE</button>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 p-3 rounded-sm text-sm font-mono-num space-y-1">
          <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(preview.subtotal)}</span></div>
          <div className="flex justify-between text-slate-600"><span>Tax</span><span>{fmtMoney(preview.tax)}</span></div>
          <div className="flex justify-between font-bold text-[#EA580C] border-t border-slate-200 pt-1"><span>Total</span><span data-testid="vpo-total-preview">{fmtMoney(preview.subtotal + preview.tax)}</span></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Payment terms"><Input data-testid="vpo-manual-payment-terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="e.g. 50% advance, balance on delivery" /></Field>
          <Field label="Delivery terms"><Input data-testid="vpo-manual-delivery-terms" value={form.delivery_terms} onChange={(e) => setForm({ ...form, delivery_terms: e.target.value })} placeholder="e.g. Within 2 weeks, at site" /></Field>
        </div>
        <Field label="Terms"><Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="Net 30, delivery at site, quality checks…" /></Field>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>

        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-3">
          <Checkbox
            id="vpo-auto-send" checked={form.auto_send_email}
            onCheckedChange={(c) => setForm({ ...form, auto_send_email: !!c })}
            data-testid="vpo-auto-send-checkbox"
            className="mt-0.5"
          />
          <Label htmlFor="vpo-auto-send" className="text-sm text-amber-900 cursor-pointer flex-1">
            <div className="font-semibold flex items-center gap-1"><EnvelopeSimple size={14} /> Email PO to vendor on save</div>
            <div className="text-xs text-amber-800 mt-0.5">A PDF copy will be attached and sent to the vendor email above. Requires SendGrid to be configured.</div>
          </Label>
        </div>

        <Button onClick={submit} data-testid="save-vpo-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Create Vendor PO</Button>
      </div>
    </SheetContent>
  );
}
